
import {
  defineComponent,
  computed,
  PropType,
  ref,
  nextTick,
  provide,
  reactive,
  toRefs,
} from 'vue';
import { ElMessage } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';

import promiseIterator from '@/core/utils/promise-iterator';
// import { promiseIterator } from '@toy/utils';
import { uploadFile } from '@/api/open';
// import { NOOP } from '@/types/utils';
import FileList from './file-list.vue';
import UploadPaste from './upload-paste.vue';
import { IFile, TFileData, IFakeFileData, TOnMove, TOnRemove } from './types';
import { uploaderKey } from './utils/token';
import logger from './utils/logger';

const isDev = process.env.NODE_ENV === 'development';

interface TipsItem {
  message: string;
  type?: 'success' | 'warning' | 'danger';
}

export default defineComponent({
  name: 'Uploader',
  components: {
    FileList,
    [Plus.name]: Plus,
  },
  props: {
    modelValue: {
      type: Array as PropType<(IFile | TFileData)[]>,
      default: () => ([]),
    },
    /**
     * 是否可以查看大图
     */
    preview: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否可以移除
     */
    remove: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否显示粘贴上传
     */
    paste: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否粘贴、拖放到面板立即上传
     */
    pasteAutoUpload: {
      type: Boolean,
      default: false,
    },
    /**
     * 禁用编辑
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否可以移动
     */
    move: {
      type: Boolean,
      default: true,
    },
    /**
     * 上传数量限制， 0为不限制
     */
    limit: {
      type: Number,
      default: 0,
    },
    /**
     * 是否支持多选文件
     */
    multiple: {
      type: Boolean,
      default: true,
    },
    /**
     * 样式大小
     */
    size: {
      type: String as PropType<'medium' | 'small' | 'mini' | 'tiny'>,
      viladator: (val: string) => {
        return ['medium', 'small', 'mini', 'tiny'].indexOf(val) !== -1;
      },
      default: 'small',
    },
    name: {
      type: String,
      default: 'file',
    },
    accept: {
      type: String,
      default: '',
    },
    /**
     * 文件超出个数限制时的钩子
     */
    onExceed: {
      type: Function,
      default: () => {
        ElMessage.warning('上传数量超过限制');
      },
    },
    beforeUpload: {
      type: Function,
      default: null,
    },
    onMove: {
      type: Function as PropType<TOnMove>,
      default: null,
    },
    onRemove: {
      type: Function as PropType<TOnRemove>,
      default: null,
    },
    onError: {
      type: Function,
      default: null,
    },
    onSuccess: {
      type: Function,
      default: null,
    },
    /**
     * 兼容旧方法，建议使用 accept
     */
    fileTypeLimit: {
      type: String,
    },
    sizeLimit: {
      type: [String, Number],
    },
    /**
     * 底部文案提示
     * tips: string | (string | TipsItem)[]
     *
     * interface TipsItem {
     *  message: string;
     *  type?: 'success' | 'warning' | 'danger';
     * }
     */
    tips: {
      type: [String, Array] as PropType<string | (string | TipsItem)[]>,
      default: '',
    },
    /**
     * 是否使用验证accept属性
     * 由于在部分系统、浏览器中是不会验证accept的正确性，用户还可以在选择时切换并使用全部文件类型
     */
    checkAccept: {
      type: Boolean,
      default: true,
    },
    /**
     * file-list的位置。
     * 只有 `prepend`、`after`。
     * 默认 prepend。
     */
    listPosition: {
      type: String,
      default: '',
      validator: (val: string) => {
        return ['prepend', 'after', ''].indexOf(val) !== -1;
      },
    },
    /**
     * 结构，这里指的是上传框和文件列表，其中上传框和文件列表里面的排列方式不影响。
     * 上下（upper-lower），左右（left-right）。
     * 默认左右。
     * 注：当listType为text时，只有上下（upper-lower）结构
     */
    structure: {
      type: String,
      default: '',
      validator: (val: string) => {
        return ['upper-lower', 'left-right', ''].indexOf(val) !== -1;
      },
    },
    /**
     * 文件列表的类型: picture-card text
     * 默认：picture-card
     */
    listType: {
      type: String,
      default: 'picture-card',
      validator: (val: string) => {
        return ['picture-card', 'text'].indexOf(val) !== -1;
      },
    },
    /*
      排列方式
    */
    direction: {
      type: String as PropType<'row' | 'column'>,
      default: 'row',
    },
    /**
     * 是否使用组件的上传wrapper(就那个边框)
     * 默认为 true，不使用必须要有slot
     */
    useWrapper: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['change', 'update:modelValue'],
  setup(props, { emit }) {
    const getFormFileData = (file: File) => {
      const formData = new FormData();
      formData.append('files', file, file.name);
      return formData;
    };

    const triggerRef = computed({
      get() {
        return props.modelValue.map(it => it.url).join(',');
      },
      set() { /** * 不设置，用于触发验证 */ },
    });

    /**
     * 上传底部提示文案
     */
    const uploadTips = computed<TipsItem[]>(() => {
      const _list: (string | TipsItem)[] = Array.isArray(props.tips) ? props.tips : [props.tips];
      return _list.map((item) => {
        if (typeof item === 'string') {
          return {
            message: item,
          };
        }
        return item;
      });
    });

    const inputRef = ref<HTMLInputElement | null>(null);
    // 临时的列表
    const fakeList = ref<IFakeFileData[]>([]);
    // 是否需要显示临时列表
    const isShowFakeList = ref(false);
    // 用于显示文件的列表
    const showFilesList = computed(() => {
      return isShowFakeList.value
        ? props.modelValue.concat(fakeList.value)
        : props.modelValue;
    });

    // 自动适配 listPosition
    const selfListPosition = computed(() => {
      if (props.listPosition) {
        return props.listPosition;
      }

      if (props.listType === 'text') {
        return 'after';
      }
      return 'prepend';
    });

    const selfStructure = computed(() => {
      if (props.structure) {
        return props.structure;
      }

      if (props.listType === 'text') {
        return 'upper-lower';
      }
      return 'left-right';
    });
    /**
     * 检测是否可以多选
     */
    const isMultiple = computed(() => {
      const { multiple, limit, modelValue } = props;
      if (multiple && limit) {
        // 当前数量大于限制两张才可以
        return limit - modelValue.length >= 2;
      }
      return multiple;
    });

    /**
     * 上传
     */
    async function uploadFiles(files: FileList) {
      const { limit, modelValue: fileList, onExceed, beforeUpload, onError, onSuccess } = props;

      if (limit && fileList.length + files.length > limit) {
        if (isDev) {
          logger.warn('上传数量超过限制');
        }
        onExceed(files, fileList);
        return;
      }

      let postFileList = Array.from(files);

      if (!postFileList.length) {
        return;
      }

      if (!isMultiple.value) {
        postFileList = postFileList.slice(0, 1);
      }

      try {
        const res = await promiseIterator(postFileList, async (file) => {
          let beforeUploadRes = true;
          // 有上传前验证
          if (beforeUpload) {
            try {
              beforeUploadRes = await beforeUpload(file);
            } catch (e) {
              beforeUploadRes = false;
            }
          } else {
            // 兼容旧用法
            // 旧上传组件验证方式
            const filename = file.name;
            const fileTypeLimit = props.fileTypeLimit ? (props.fileTypeLimit.toLowerCase()).split('|') : [];
            const fileType = filename.slice(filename.lastIndexOf('.') + 1).toLowerCase();

            let isFileTye = true;
            // 校验文件 类型是否满足要起
            if (fileTypeLimit.length) {
              logger.warn('建议使用accept限制上传文件类型');
              isFileTye = fileTypeLimit.some(it => it === fileType);
            }
            let isSizeLimit = true;
            if (props.sizeLimit) {
              isSizeLimit = file.size / 1024 / 1024 < parseFloat(`${props.sizeLimit}`);
            }

            if (!isFileTye) {
              ElMessage.warning(`请上传指定格式(${fileTypeLimit.join(',')})的文件`);
            } else if (!isSizeLimit) {
              ElMessage.warning(`上传图片大小不能超过 ${parseFloat(`${props.sizeLimit}`)}MB!`);
            }
            // 检测accept匹配，不通过静默不上传
            // 取消主动验证
            const passAccept = props.accept && props.checkAccept
              ? props.accept.split(',').some((match) => {
                const _match = match.trim();
                if (_match === '*') {
                  return true;
                }
                const _matchList = _match.split('/');
                // 文件类型
                const _fileType = _matchList[_matchList.length - 1];
                // 最后是*
                const isAll = _fileType === '*';

                if (isAll) {
                  const flag = _matchList[0] === file.type.split('/')[0];

                  if (!flag) {
                    logger.warn(`accept验证失败：限制类型 - ${_matchList[0]}; 文件类型 - ${file.type.split('/')[0]}`);
                  }
                  return flag;
                }
                // 直接使用文件格式
                if (_fileType.startsWith('.')) {
                  if (file.type) {
                    const typeIndex = file.type.indexOf('/');
                    const thisFileType = typeIndex > -1
                      ? file.type.slice(typeIndex + 1)
                      : file.type;
                    const flag = _fileType.slice(1).toLocaleLowerCase() === thisFileType.toLocaleLowerCase();

                    if (!flag) {
                      logger.warn(`accept验证失败：限制类型 - ${_fileType.slice(1)}; 文件类型 - ${thisFileType}`);
                    }
                    return flag;
                  }
                  /**
                   * 文件的type没有就直接用文件后缀算了
                   */
                  const __fileNameInfos = file.name?.split('.');
                  // 获取文件名称后缀
                  const nameType = __fileNameInfos[__fileNameInfos.length - 1] ?? '';
                  if (nameType) {
                    const flag = _fileType.slice(1).toLocaleLowerCase() === nameType.toLocaleLowerCase();
                    if (!flag) {
                      logger.warn(`accept验证失败：限制类型 - ${_fileType.slice(1)}; 文件类型 - ${nameType}`);
                    }
                    return flag;
                  }

                  return true;
                }

                const flag = _match === file.type;

                if (!flag) {
                  logger.warn(`accept验证失败：限制类型 - ${_match}; 文件类型 - ${file.type}`);
                }
                return flag;
              })
              : true;

            if (!passAccept) {
              ElMessage.warning('请上传指定格式的文件');
            }

            beforeUploadRes = isSizeLimit && isFileTye && passAccept;
          }

          if (beforeUploadRes) {
            // 有上传，可以显示临时数据
            isShowFakeList.value = true;
            // 当前的下标
            const index = fakeList.value.length;
            fakeList.value.push({
              loading: true,
              name: file.name,
              size: `${file.size}`,
              type: file.type,
              // 表示这个占位的图
              fake: true,
              url: URL.createObjectURL(file),
            });

            const { data } = await uploadFile(getFormFileData(file));
            const _fileRes = Object.assign({
              type: file.type,
            }, data[0]);
            onSuccess?.(_fileRes, files, postFileList);
            // 替换当前临时数据
            fakeList.value.splice(index, 1, _fileRes);
            return _fileRes;
          }
          return null;
        });

        const _fileDataList = res.filter(Boolean) as TFileData[];
        const returnData = fileList.concat(_fileDataList);

        emit('update:modelValue', returnData);

        emit('change', returnData);
      } catch (e: any) {
        logger.error(e);
        onError?.(e, files, postFileList);
      } finally {
        nextTick(() => {
          isShowFakeList.value = false;
          fakeList.value = [];
        });
      }
    }

    /**
     * 处理文件选择
     */
    const handleFilePick = (files?: FileList | null) => {
      if (files?.length) {
        uploadFiles(files);
      }

      nextTick(() => {
        const _inputRef = inputRef.value!;
        _inputRef.value = '';
      });
    };

    /**
     * 处理change事件
     */
    const handleIputChange = (e: Event) => {
      const { files } = e.target as HTMLInputElement;
      handleFilePick(files);
    };

    const uploaderRef = reactive({
      emit,
      inputRef,
      ...toRefs(props),
      uploadFiles,
    });

    provide(uploaderKey, uploaderRef);

    return {
      inputRef,

      isMultiple,

      selfListPosition,

      selfStructure,

      showFilesList,

      handleIputChange,
      uploadFiles,

      handleIputDrop(e: DragEvent) {
        const { files } = e.dataTransfer!;
        handleFilePick(files);

        e.preventDefault();
      },

      uploadTips,

      triggerRef,
    };
  },
  render() {
    const upLoadWrapClassName = `upload-wrap__${this.size}`;

    const hasTipRender = () => {
      return !!this.$slots.default;
    };

    const tipRender = () => {
      if (hasTipRender()) {
        return (<div class="tip-wrapper">
            {
              this.$slots.default?.()
            }
          </div>);
      }
      return (
        <el-icon color="#8c939d" size={28}>
          <plus />
        </el-icon>
      );
    };

    const fileListRender = () => {
      const style = {} as { [k: string]: string; };

      if (this.selfStructure === 'upper-lower') {
        style.overflow = 'hidden';
      }
      return (
        this.$slots.list
          ? this.$slots.list(this.showFilesList)
          : <file-list
              v-show={ this.showFilesList.length }
              list={ this.showFilesList }
              list-type={ this.$props.listType }
              style={style}
          />
      );
    };

    const uploaderRender = () => {
      return (
        <input
          type="file"
          ref="inputRef"
          class="upload-input"
          onDrop={ this.handleIputDrop }
          onChange={ this.handleIputChange }
          multiple={ this.isMultiple }
          name={ this.name }
          accept={ this.accept }
          disabled={ this.disabled }
        />
      );
    };

    const uploadWrapperRender = () => {
      return [
        <div
          class={['uploaders', {
            'float-left': this.selfStructure === 'left-right',
          }]}
          v-show={
            this.$props.limit > 0
              ? this.showFilesList.length < this.$props.limit
              : true
          }>
          <div
            class={
              ['upload-wrap', upLoadWrapClassName, 'handle-wrap']
            }
            v-show={ !this.disabled }
          >
            {
              uploaderRender()
            }
            {
              // 自定义提示
              tipRender()
            }
          </div>
          {
            this.paste
              ? <UploadPaste class="handle-wrap" />
              : null
          }
        </div>,
      ];
    };

    return (
      <div>
        {
          /**
           * 关于 this.selfListPosition 的两个啰嗦判断
           * 是因为没有更好的方法，所以啰嗦的写了两次
           */
        }
        <div class={['wrapper', this.direction]}>
          {
            /**
             * 用于触发 el-form 的表单验证
             */
          }
          <el-input style="display:none" v-model={this.triggerRef} />
          {
            this.selfListPosition === 'prepend'
              ? fileListRender()
              : null
          }
          {
            /**
             * 判断是否使用自定义容器
             */
            !this.$props.useWrapper && hasTipRender()
              ? <div class="auto-wrapper">
                  {
                    uploaderRender()
                  }
                  {
                    tipRender()
                  }
                </div>
              : uploadWrapperRender()
          }
          {
            this.selfListPosition === 'after'
              ? fileListRender()
              : null
          }
        </div>
        {
          this.$slots.tips
            ? this.$slots.tips()
            : this.uploadTips.filter(Boolean).map((_tips, i) => {
              return (
                <div
                  key={i}
                  class={['upload-tips', _tips.type]}
                >
                  { _tips.message}
                </div>
              );
            })
        }
      </div>
    );
  },
});
